import React, { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import type { PickemDetailResponse } from '@low6-pickem/api-types';
import type { Competition } from '@/types/domain';
import GameCardFullWidth from '@/components/game/GameCardFullWidth';
import ReservePanel from '@/components/game/ReservePanel';
import ConfirmEntryModal from '@/components/modals/ConfirmEntryModal';
import EntrySubmittedModal from '@/components/modals/EntrySubmittedModal';
import ContestDetailsModal from '@/components/modals/ContestDetailsModal';
import { AdSlot } from '@/components/ads/AdSlot';
import { usePickemContestCardAd } from '@/hooks/usePickemContestCardAd';
import { useReservePickem } from '@/api/mutations/useReservePickem';
import { getLeagueLogoUrl } from '@/utils/pickem-helpers';

interface ReserveEntryScreenProps {
  gameId: string;
  pickemDetails: PickemDetailResponse | undefined;
  competitionData: Competition | (Partial<Competition> & { id: string; title: string });
  onBack: () => void;
  onNavigateUpcoming: () => void;
  onNavigateLobby: () => void;
}

/**
 * "Reserve Entry" screen (DKPLAT-528) — rendered instead of the normal picks
 * flow when a pickem has no questions posted yet. Lets the user reserve a
 * free seat ahead of questions being published.
 */
const ReserveEntryScreen: React.FC<ReserveEntryScreenProps> = ({
  gameId,
  pickemDetails,
  competitionData,
  onBack,
  onNavigateUpcoming,
  onNavigateLobby,
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSubmittedModal, setShowSubmittedModal] = useState(false);
  const [showContestDetails, setShowContestDetails] = useState(false);
  const [reservationId, setReservationId] = useState<string | null>(null);

  const { adPlacement, shouldShowAd } = usePickemContestCardAd(gameId);

  const reserveMutation = useReservePickem({
    pickemId: gameId,
    onSuccess: data => {
      setReservationId(data.id);
      setShowConfirmModal(false);
      setShowSubmittedModal(true);
    },
  });

  const hasReservation =
    'hasReservation' in competitionData ? !!competitionData.hasReservation : false;
  const questionsPostedAt =
    'questionsPostedAt' in competitionData ? (competitionData.questionsPostedAt ?? null) : null;

  const totalQuestions =
    pickemDetails?.pickem.games_count ??
    new Set(
      (pickemDetails?.pickem.questions ?? []).map(q => q.sport_match_data?.id).filter(Boolean)
    ).size;

  const gameCardData = pickemDetails
    ? {
        id: pickemDetails.pickem.pickem_id,
        title: competitionData.title,
        prize: pickemDetails.pickem.prize_text_en || competitionData.prize || '$500',
        state: (competitionData.state ?? 'countdown') as Competition['state'],
        gameDate: new Date(
          competitionData.gameDate ?? pickemDetails.pickem.start_utc ?? Date.now()
        ),
        totalPicks: totalQuestions,
        leagueLogoUrl: getLeagueLogoUrl(pickemDetails.pickem.sport as string),
        totalEntries: competitionData.totalEntries ?? pickemDetails.pickem.entries ?? 0,
        entryCost: 'FREE',
        competitionData,
        showLeaderBoard: false,
        showResults: false,
      }
    : null;

  const handleEnterClick = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = () => {
    reserveMutation.mutate();
  };

  const handleGoToUpcoming = () => {
    setShowSubmittedModal(false);
    onNavigateUpcoming();
  };

  const handleBackToLobby = () => {
    setShowSubmittedModal(false);
    onNavigateLobby();
  };

  return (
    <div data-test-id="reserve-entry-screen" className="flex flex-col min-h-screen">
      {/* Back-chevron header — same pattern as other detail screens (e.g. ContestDetailsModal) */}
      <div className="bg-app-background sticky top-0 z-10 px-4 py-4 flex items-center border-b border-border/30">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          data-test-id="reserve-entry-back-btn"
          className="p-2 -ml-2 text-text-secondary hover:text-text-primary transition-colors"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <h1 className="flex-1 text-center text-text-primary text-xl font-bold mr-10">
          Reserve Entry
        </h1>
      </div>

      <div className="flex-1 px-4 pt-4 pb-8 space-y-4">
        {gameCardData && <GameCardFullWidth {...gameCardData} />}

        <button
          type="button"
          onClick={() => setShowContestDetails(true)}
          data-test-id="reserve-entry-pay-table-link"
          className="text-pickem-primary text-sm font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity"
        >
          Pay Table and Rules
        </button>

        {shouldShowAd && adPlacement && (
          <AdSlot
            divId={`reserve-entry-${gameId}`}
            adPlacement={adPlacement}
            placement="reserve-entry"
            className="rounded-lg overflow-hidden"
          />
        )}

        <ReservePanel
          id={gameId}
          title={competitionData.title}
          prize={pickemDetails?.pickem.prize_text_en || competitionData.prize || '$500'}
          questionsPostedAt={questionsPostedAt}
          hasReservation={hasReservation}
          isSubmitting={reserveMutation.isPending}
          onEnter={handleEnterClick}
        />
      </div>

      <ConfirmEntryModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSubmit}
        isSubmitting={reserveMutation.isPending}
      />

      <EntrySubmittedModal
        isOpen={showSubmittedModal}
        onClose={() => setShowSubmittedModal(false)}
        reservationId={reservationId}
        onGoToUpcoming={handleGoToUpcoming}
        onBackToLobby={handleBackToLobby}
      />

      <ContestDetailsModal
        isOpen={showContestDetails}
        onClose={() => setShowContestDetails(false)}
        pickemDetails={pickemDetails}
        competitionData={competitionData}
      />
    </div>
  );
};

export default ReserveEntryScreen;
